import { isSentryEnabled } from '@/lib/sentry';
import * as Sentry from '@sentry/react';
/**
 * The study-load waterfall, as Sentry spans.
 *
 * `browserTracingIntegration` at its defaults produces a pageload transaction that
 * idle-closes about a second after the route settles — long before a study of any size has
 * finished arriving. Everything that matters about a slow viewer open (the per-series
 * fetches, the prefetch passes, the wait the reader actually experienced) therefore happened
 * after the only transaction had already ended, and attached to nothing. Sentry could say a
 * page loaded; it could not say the read was slow.
 *
 * So the study open gets an explicit span of its own, opened by hand at viewer open and
 * closed at study-complete-or-abandoned, with one child per series. It is an INACTIVE span
 * deliberately — it must not become the ambient parent of every unrelated fetch, click and
 * error raised during a multi-minute read.
 *
 * Every function here is a no-op when Sentry has no DSN (local dev, tests), so nothing in
 * the viewer needs its own guard.
 */
const STUDY_LOAD_SPAN = 'viewer.study_load';
const SERIES_LOAD_SPAN = 'viewer.series_load';
let studySpan = null;
const seriesSpans = new Map();
/**
 * Open the study-load span. A study already in flight is closed as abandoned first — the
 * reader moved on from it, which is exactly what the outcome says.
 */
export function startStudyLoadTrace(attributes) {
    if (!isSentryEnabled())
        return;
    endStudyLoadTrace('abandoned');
    studySpan = Sentry.startInactiveSpan({
        name: STUDY_LOAD_SPAN,
        op: STUDY_LOAD_SPAN,
        attributes: {
            seriesCount: attributes.seriesCount,
            ...(attributes.imageCount === undefined ? {} : { imageCount: attributes.imageCount }),
            ...(attributes.modality === undefined ? {} : { modality: attributes.modality }),
        },
    });
}
/** Open a per-series child span. Silently ignored when no study span is open. */
export function startSeriesLoadTrace(seriesId) {
    if (!isSentryEnabled() || !studySpan)
        return;
    if (seriesSpans.has(seriesId))
        return;
    seriesSpans.set(seriesId, Sentry.startInactiveSpan({
        name: SERIES_LOAD_SPAN,
        op: SERIES_LOAD_SPAN,
        parentSpan: studySpan,
    }));
}
/** Close one series' child span at its genuine completion. */
export function endSeriesLoadTrace(seriesId) {
    const span = seriesSpans.get(seriesId);
    if (!span)
        return;
    seriesSpans.delete(seriesId);
    span.end();
}
/**
 * Close the study span with its outcome. Any series still in flight is closed as abandoned
 * alongside it — an unfinished child span would otherwise dangle past its parent, which
 * Sentry drops rather than displays.
 */
export function endStudyLoadTrace(outcome) {
    for (const [seriesId, span] of seriesSpans) {
        seriesSpans.delete(seriesId);
        span.setAttribute('outcome', 'abandoned');
        span.end();
    }
    if (!studySpan)
        return;
    const span = studySpan;
    // Cleared before ending so a re-entrant call (study switch, hide-then-unmount) can never
    // end the same span twice.
    studySpan = null;
    span.setAttribute('outcome', outcome);
    // Only a completed load gets an explicit OK; an abandoned one is left UNSET rather than
    // marked ERROR. A reader closing a slow study is not a failure of the system in the sense
    // Sentry's failure-rate charts mean, and filing it as one would bury real errors under it.
    if (outcome === 'complete')
        span.setStatus({ code: 1, message: 'ok' });
    span.end();
}
